import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import type { ColDef } from "ag-grid-enterprise";
import moment from "moment";
import type { DelayByShift } from "../models/DelayByShift";

const compareDates = (dateA: Date | null, dateB: Date | null) => {
	if (dateA === dateB) return 0;
	if (dateA === null) return 1;
	if (dateB === null) return -1;
	return dateA.getTime() - dateB.getTime();
};

export const useTableData = () => {
	const columns: ColDef<DelayByShift>[] = [
		{
			field: "Start",
			headerName: "Date",
			valueGetter: (params) =>
				dxtToLocalServerTime(params.data?.Start, "MM/dd") || "",
			comparator: (_valueA, _valueB, nodeA, nodeB) => {
				const startComparison = compareDates(
					nodeA.data?.Start ?? null,
					nodeB.data?.Start ?? null,
				);

				return startComparison !== 0
					? startComparison
					: compareDates(nodeA.data?.End ?? null, nodeB.data?.End ?? null);
			},
			minWidth: 60,
			flex: 1,
			sortable: true,
		},
		{
			field: "AssetName",
			headerName: "Asset",
			minWidth: 60,
			flex: 1,
			sortable: true,
		},
		{
			field: "Shift",
			headerName: "Shift",
			valueGetter: (params) => params.data?.Shift || "",
			minWidth: 60,
			flex: 1,
			sortable: true,
		},
		{
			field: "Crew",
			headerName: "Crew",
			valueGetter: (params) => params.data?.Crew || "",
			minWidth: 60,
			flex: 1,
		},
		{
			field: "SectionName",
			headerName: "Section",
			valueGetter: (params) => params.data?.SectionName || "",
			minWidth: 60,
			flex: 1,
		},
		{
			field: "ProductionEventId",
			headerName: "Event",
			valueFormatter: (params) =>
				params.value == null ? "" : `#${params.value}`,
			minWidth: 60,
			flex: 1,
			sortable: true,
		},
		{
			field: "Start",
			headerName: "Start",
			valueGetter: (params) =>
				dxtToLocalServerTime(params.data?.Start, "HH:mm:ss") || "",
			minWidth: 60,
			flex: 1,
			sortable: true,
		},
		{
			field: "End",
			headerName: "End",
			valueGetter: (params) =>
				dxtToLocalServerTime(params.data?.End, "HH:mm:ss") || "",
			minWidth: 80,
			flex: 1,
			sortable: true,
		},
		{
			field: "Duration",
			headerName: "Duration",
			valueFormatter: (params) => {
				if (params.value) {
					const duration = moment.duration(params.value, "seconds");
					const minutes = Math.floor(duration.asMinutes());
					const seconds = duration.seconds();

					const formattedMinutes = String(minutes).padStart(2, "0");
					const formattedSeconds = String(seconds).padStart(2, "0");

					return `${formattedMinutes}m ${formattedSeconds}s`;
				}
				return "";
			},
			minWidth: 60,
			flex: 1,
			sortable: true,
		},
		{
			field: "DelayCodePath",
			headerName: "Code",
			valueGetter: (params) => {
				const delayCodeName = params.data?.DelayCodeName;
				const delayCodePath = params.data?.DelayCodePath;

				const isSystemDelayCode =
					delayCodeName === "Adjustment" ||
					delayCodeName === "Undeclared Mill" ||
					delayCodeName === "Undeclared Cold Shear";

				let displayPath = delayCodePath;
				const maxLength = 40;

				// If the path is longer than maxLength, intelligently truncate it
				if (delayCodePath && delayCodePath.length > maxLength) {
					let remainingPath = delayCodePath;

					// As long as the path is too long and has bars
					while (
						remainingPath.length > maxLength &&
						remainingPath.includes("/")
					) {
						// Delete the first segment of the path (up to the first bar)
						const firstSlashPos = remainingPath.indexOf("/");
						remainingPath = remainingPath.substring(firstSlashPos + 1);
					}

					// If we manage to reduce the path, add the prefix ".../"
					if (remainingPath !== delayCodePath) {
						displayPath = ".../" + remainingPath;
					}
				}

				return isSystemDelayCode ? delayCodeName : displayPath;
			},
			flex: 2,
			cellStyle: {
				justifyContent: "flex-start",
			},
			minWidth: 240,
		},
		{
			field: "DelayCategoryName",
			headerName: "Category",
			minWidth: 100,
			editable: false,
			flex: 1,
			sortable: true,
		},
		{
			field: "Comments",
			headerName: "Comments",
			minWidth: 150,
			flex: 4,
			sortable: true,
			cellStyle: {
				justifyContent: "flex-start",
			},
			wrapText: true,
			autoHeight: true,
		},
		{
			field: "JustifiedByUser",
			headerName: "User",
			minWidth: 80,
			editable: false,
			flex: 1,
			sortable: true,
		},
		{
			field: "JustifiedDatetime",
			headerName: "Justification",
			valueGetter: (params) =>
				dxtToLocalServerTime(params.data?.JustifiedDatetime, "HH:mm:ss") || "",
			minWidth: 120,
			flex: 1,
			sortable: true,
		},
	];

	return { columns };
};
